import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * The swirling mass: a monochrome particle nebula with a vertex-shader curl
 * twist. Sits behind everything, always moving, never loud.
 */
export const SwirlingMass = ({ density = 7000, intensity = 1 }) => {
  const host = useRef(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, 1, 0.1, 200);
    camera.position.set(0, 0, 26);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    el.appendChild(renderer.domElement);

    const count = density;
    const pos = new Float32Array(count * 3);
    const seed = new Float32Array(count);
    const sz = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const r = 3 + Math.pow(Math.random(), 0.55) * 16;
      const a = Math.random() * Math.PI * 2;
      const h = (Math.random() - 0.5) * 9 * (1 - r / 26);
      pos[i * 3] = Math.cos(a) * r;
      pos[i * 3 + 1] = h;
      pos[i * 3 + 2] = Math.sin(a) * r;
      seed[i] = Math.random();
      sz[i] = 0.6 + Math.random() * 2.2;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    geo.setAttribute("aSeed", new THREE.BufferAttribute(seed, 1));
    geo.setAttribute("aSize", new THREE.BufferAttribute(sz, 1));

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: { uTime: { value: 0 }, uIntensity: { value: intensity } },
      vertexShader: `
        attribute float aSeed;
        attribute float aSize;
        uniform float uTime;
        varying float vA;
        void main() {
          vec3 p = position;
          float r = length(p.xz);
          float spin = uTime * (0.34 / (0.6 + r * 0.14));
          float c = cos(spin), s = sin(spin);
          p.xz = mat2(c, -s, s, c) * p.xz;
          p.y += sin(uTime * 0.5 + aSeed * 12.0 + r * 0.32) * 1.5 * (1.0 - r / 26.0);
          p.x += sin(uTime * 0.25 + aSeed * 20.0) * 0.7;
          p.z += cos(uTime * 0.21 + aSeed * 16.0) * 0.7;
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_Position = projectionMatrix * mv;
          gl_PointSize = aSize * (220.0 / -mv.z);
          vA = (0.08 + 0.92 * pow(1.0 - clamp(r / 22.0, 0.0, 1.0), 1.7)) * (0.35 + 0.65 * aSeed);
        }
      `,
      fragmentShader: `
        uniform float uIntensity;
        varying float vA;
        void main() {
          vec2 d = gl_PointCoord - 0.5;
          float m = smoothstep(0.5, 0.0, length(d));
          gl_FragColor = vec4(vec3(1.0), m * vA * 0.9 * uIntensity);
        }
      `,
    });

    const points = new THREE.Points(geo, mat);
    points.rotation.x = 0.42;
    scene.add(points);

    const resize = () => {
      const w = el.clientWidth || 1;
      const h = el.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);

    const t0 = performance.now();
    let raf;
    const tick = () => {
      mat.uniforms.uTime.value = (performance.now() - t0) / 1000;
      points.rotation.y += 0.0004;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [density, intensity]);

  return (
    <div
      ref={host}
      data-testid="swirling-mass-canvas"
      className="absolute inset-0 z-0 pointer-events-none"
    />
  );
};

export default SwirlingMass;
